import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Button,
  Textarea,
  Spinner,
  Title3,
  Body1,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Send24Regular,
  ArrowLeft24Regular,
  Bot24Regular,
  LightbulbFilament24Regular,
  BoxMultiple24Regular,
  Money24Regular,
  People24Regular,
  PersonAccounts24Regular,
  ChevronUp20Regular,
  ChevronDown20Regular,
  Chat24Regular,
} from '@fluentui/react-icons';
import { useAuth } from '../auth';
import { agentService } from '../services/agentService';
import { adminService } from '../services/adminService';
import { MessageBubble } from './MessageBubble';
import { PromptList } from './PromptList';
import { AddPromptModal } from './AddPromptModal';
import { EditPromptModal } from './EditPromptModal';
import { AreaConfig, ChatMessage, PromptSuggestion, StreamChunk, Prompt } from '../types';
import { configService } from '../services/configService';
import { deletePrompt } from '../services/customPromptsService';
import { parsePromptParameters, substituteParameters, injectCompanyParameter, PromptParameter } from '../utils/promptParser';
import { generateParameterCollectionCard } from '../utils/adaptiveCardGenerator';

// Map icon names from JSON to actual icon components
const iconMap: Record<string, React.ReactElement> = {
  BoxMultiple: <BoxMultiple24Regular />,
  Money: <Money24Regular />,
  People: <People24Regular />,
  PersonAccounts: <PersonAccounts24Regular />,
  Chat: <Chat24Regular />,
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f2f2f2',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f2f2f2',
    overflowY: 'auto',
  },
  // Welcome/Prompt Selection Area
  welcomeArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 24px',
    overflowY: 'auto',
  },
  welcomeIcon: {
    width: '56px',
    height: '56px',
    marginBottom: '16px',
  },
  welcomeTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: '8px',
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '32px',
    textAlign: 'center',
    maxWidth: '500px',
  },
  // Suggested Prompt Cards in Welcome Area
  promptCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    maxWidth: '900px',
    width: '100%',
    marginBottom: '32px',
  },
  promptCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0, 120, 212, 0.15)',
      border: '1px solid #0078d4',
      transform: 'translateY(-2px)',
    },
  },
  promptCardTitle: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#1a1a2e',
    marginBottom: '8px',
  },
  promptCardText: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.4',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  // Messages Area (when chatting)
  messagesArea: {
    flex: 1,
    minHeight: '200px',
    overflowY: 'auto',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    backgroundColor: '#f2f2f2',
  },
  messagesInner: {
    maxWidth: '900px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  // Input Area
  inputWrapper: {
    backgroundColor: '#ffffff',
    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    borderTop: '1px solid #e0e0e0',
  },
  inputContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    maxWidth: '900px',
    margin: '0 auto',
    alignItems: 'flex-end',
  },
  inputField: {
    flex: 1,
    '& textarea': {
      borderRadius: '12px',
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
      fontSize: '16px',
      minHeight: '60px',
      maxHeight: '150px',
      resize: 'none',
      border: '2px solid #e0e0e0',
      lineHeight: '1.5',
      ':focus': {
        border: '2px solid #0078d4',
        outline: 'none',
      },
    },
  },
  sendButton: {
    background: 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)',
    minWidth: '100px',
    height: '48px',
    borderRadius: '12px',
    ':hover': {
      background: 'linear-gradient(135deg, #106ebe 0%, #005a9e 100%)',
    },
    ':disabled': {
      background: '#a4a4a4',
    },
  },
  // Prompt Library Section at Bottom
  promptLibrarySection: {
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  promptLibraryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    color: '#1a1a2e',
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  promptLibraryIcon: {
    color: '#0078d4',
  },
  promptLibraryTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a2e',
    flex: 1,
  },
  promptLibraryToggle: {
    color: '#666',
  },
  promptsWrapper: {
    maxHeight: '250px',
    overflowY: 'auto',
    transition: 'max-height 0.3s ease, opacity 0.2s ease',
  },
  promptsWrapperCollapsed: {
    maxHeight: '0',
    overflow: 'hidden',
    opacity: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalM,
    height: '100%',
    backgroundColor: '#f2f2f2',
  },
});

interface ChatInterfaceProps {
  area: AreaConfig;
  onBack: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ area, onBack }) => {
  const styles = useStyles();
  const { getAccessToken } = useAuth();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isStartingConversation, setIsStartingConversation] = useState(true);
  const [isPromptLibraryCollapsed, setIsPromptLibraryCollapsed] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<{
    prompt: PromptSuggestion;
    parameters: PromptParameter[];
  } | null>(null);
  const [companyCode, setCompanyCode] = useState<string>('USMF');
  const [isAddPromptModalOpen, setIsAddPromptModalOpen] = useState(false);
  const [isEditPromptModalOpen, setIsEditPromptModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);
  const [areas, setAreas] = useState<AreaConfig[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load areas on mount
  useEffect(() => {
    const loadAreas = async () => {
      try {
        const areasData = await configService.getAreas();
        setAreas(areasData);
      } catch (error) {
        console.error('Failed to load areas:', error);
      }
    };
    loadAreas();
  }, []);

  // Load company code on mount
  useEffect(() => {
    const loadCompanyCode = async () => {
      try {
        const code = await adminService.getCompanyCode();
        setCompanyCode(code);
      } catch (error) {
        console.error('Failed to load company code:', error);
        // Keep default 'USMF'
      }
    };
    loadCompanyCode();
  }, []);

  // Start conversation on mount
  useEffect(() => {
    const initConversation = async () => {
      try {
        setIsStartingConversation(true);
        const response = await agentService.startConversation(getAccessToken);
        setConversationId(response.conversationId);
        
        // Add welcome message
        setMessages([
          {
            id: 'welcome',
            text: response.message,
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
      } catch (error) {
        console.error('Failed to start conversation:', error);
        setMessages([
          {
            id: 'error',
            text: 'Failed to connect to the assistant. Please try again.',
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsStartingConversation(false);
      }
    };

    initConversation();
  }, [getAccessToken]);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || !conversationId || isLoading) return;

    setHasStartedChat(true);
    setIsLoading(true);

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    // Add placeholder for bot response
    const botMessageId = `bot-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      },
    ]);

    try {
      await agentService.sendMessage(
        {
          message: messageText,
          conversationId,
          area: area.id,
        },
        getAccessToken,
        (chunk: StreamChunk) => {
          console.log('Received chunk:', chunk);
          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id !== botMessageId) return msg;
              
              // If chunk has an Adaptive Card, show the card
              if (chunk.adaptiveCard) {
                return {
                  ...msg,
                  text: '',
                  adaptiveCard: chunk.adaptiveCard,
                  isStreaming: false,
                };
              }
              
              // If this is the final message, REPLACE the text entirely
              // (Final message is usually better formatted than streaming chunks)
              if (chunk.isFinalMessage && chunk.text) {
                console.log('Replacing with final message:', chunk.text.length, 'chars');
                return {
                  ...msg,
                  text: chunk.text,
                  isStreaming: false,
                };
              }
              
              // For streaming chunks, REPLACE text (Copilot sends cumulative text)
              const newText = chunk.text || msg.text;
              console.log('Streaming text length:', newText.length);
              return {
                ...msg,
                text: newText,
                isStreaming: !chunk.isComplete,
              };
            })
          );
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                text: 'Sorry, I encountered an error. Please try again.',
                isStreaming: false,
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, isLoading, area.id, getAccessToken]);

  const handleEditPrompt = (prompt: PromptSuggestion) => {
    setEditingPrompt(prompt as Prompt);
    setIsEditPromptModalOpen(true);
  };

  const handleDeletePrompt = async (prompt: PromptSuggestion) => {
    if (!prompt.isCustom) {
      console.error('Cannot delete standard prompts');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${prompt.title}"?`)) {
      setDeletingPromptId(prompt.id);
      try {
        await deletePrompt(prompt.id);
        // Reload areas to reflect deletion
        configService.clearCache();
        const areasData = await configService.getAreas();
        setAreas(areasData);
        // Force page reload to refresh the current area
        window.location.reload();
      } catch (error) {
        console.error('Failed to delete prompt:', error);
        alert('Failed to delete prompt: ' + (error instanceof Error ? error.message : 'Unknown error'));
      } finally {
        setDeletingPromptId(null);
      }
    }
  };

  const handlePromptSelect = (prompt: PromptSuggestion) => {
    console.log('Prompt selected:', prompt.text);
    console.log('Conversation ID:', conversationId);
    console.log('Is Loading:', isLoading);

    if (!conversationId) {
      console.error('No conversation ID yet');
      return;
    }

    // Parse parameters from prompt
    const parameters = parsePromptParameters(prompt.text);
    console.log('📊 Parsed parameters:', parameters);
    console.log('📊 Parameters count:', parameters.length);

    if (parameters.length > 0) {
      console.log('✨ Showing parameter collection card for:', parameters.map(p => p.name));
      // Show parameter collection card
      setHasStartedChat(true); // 🔥 FIX: Switch to messages area!
      setPendingPrompt({ prompt, parameters });
      const cardJson = generateParameterCollectionCard(parameters, prompt.title);
      console.log('📝 Generated card JSON:', cardJson);

      // Add card message to chat
      setMessages((prev) => [
        ...prev,
        {
          id: `card-${Date.now()}`,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          adaptiveCard: { type: 'AdaptiveCard', cardJson },
        },
      ]);
    } else {
      // No parameters - send directly with company injection
      const finalText = injectCompanyParameter(prompt.text, companyCode);
      sendMessage(finalText);
    }
  };

  const handleCardAction = useCallback(async (action: string, data: any) => {
    if (!conversationId || isLoading) return;

    console.log('Card action received:', action, data);

    // Handle parameter submission from our generated cards
    if (action === 'submitParameters' && pendingPrompt) {
      const { prompt, parameters } = pendingPrompt;

      // Extract values from card data
      const values: Record<string, string> = {};
      for (const param of parameters) {
        values[param.name] = data[param.name] || '';
      }

      // Remove the parameter collection card from messages
      setMessages((prev) => prev.filter((msg) => !msg.adaptiveCard || msg.adaptiveCard.type !== 'AdaptiveCard' || !msg.id.startsWith('card-')));

      // Substitute parameters and inject company
      let finalText = substituteParameters(prompt.text, parameters, values);
      finalText = injectCompanyParameter(finalText, companyCode);

      setPendingPrompt(null);
      sendMessage(finalText);
      return;
    }

    // Handle MCP card actions (consent, etc.)
    setIsLoading(true);

    // Add a placeholder for the response
    const botMessageId = `bot-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: botMessageId,
        text: action === 'Allow' ? 'Connecting...' : 'Cancelling...',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      },
    ]);

    try {
      await agentService.sendCardAction(
        {
          conversationId,
          action,
          data,
        },
        getAccessToken,
        (chunk: StreamChunk) => {
          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id !== botMessageId) return msg;
              
              // If chunk has an Adaptive Card, show the card
              if (chunk.adaptiveCard) {
                return {
                  ...msg,
                  text: '',
                  adaptiveCard: chunk.adaptiveCard,
                  isStreaming: false,
                };
              }
              
              // Otherwise show the text
              return {
                ...msg,
                text: chunk.text || msg.text,
                isStreaming: !chunk.isComplete,
              };
            })
          );
        }
      );
    } catch (error) {
      console.error('Failed to process card action:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                text: 'Failed to process your response. Please try again.',
                isStreaming: false,
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, isLoading, getAccessToken, pendingPrompt, companyCode, sendMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  if (isStartingConversation) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Starting conversation..." />
        </div>
      </div>
    );
  }

  // Get first 6 prompts for the card grid
  const topPrompts = area.prompts.slice(0, 6);

  return (
    <div className={styles.container}>
      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Welcome Area with Prompt Cards (when no chat started) */}
        {!hasStartedChat ? (
          <div className={styles.welcomeArea}>
            <img 
              src="/lightbulb-icon.png" 
              alt="Ideas" 
              className={styles.welcomeIcon}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <LightbulbFilament24Regular style={{ fontSize: '48px', color: '#0078d4', marginBottom: '16px' }} />
            <div className={styles.welcomeTitle}>{area.name}</div>
            <div className={styles.welcomeSubtitle}>
              Try one of the suggested prompts below to get started or review the prompt library below for additional ideas for your role.
            </div>
            
            {/* Prompt Cards Grid */}
            <div className={styles.promptCardsGrid}>
              {topPrompts.map((prompt) => (
                <div 
                  key={prompt.id}
                  className={styles.promptCard}
                  onClick={() => handlePromptSelect(prompt)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handlePromptSelect(prompt);
                    }
                  }}
                >
                  <div className={styles.promptCardTitle}>{prompt.title}</div>
                  <div className={styles.promptCardText}>{prompt.text}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Messages Area - Shows conversation */
          <div className={styles.messagesArea}>
            <div className={styles.messagesInner}>
              {messages.map((message) => (
                <MessageBubble 
                  key={message.id} 
                  message={message} 
                  onCardAction={handleCardAction}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Chat Input */}
        <div className={styles.inputWrapper}>
          <form onSubmit={handleSubmit} className={styles.inputContainer}>
            <Textarea
              className={styles.inputField}
              placeholder={`Ask a question about ${area.name.toLowerCase()}...`}
              value={inputValue}
              onChange={(e, data) => setInputValue(data.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              size="large"
              resize="vertical"
              rows={2}
            />
            <Button
              appearance="primary"
              icon={isLoading ? <Spinner size="tiny" /> : <Send24Regular />}
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className={styles.sendButton}
            >
              Send
            </Button>
          </form>
        </div>

        {/* Prompt Library Section - Always visible, collapsible */}
        <div className={styles.promptLibrarySection}>
          <div
            className={styles.promptLibraryHeader}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div
              onClick={() => setIsPromptLibraryCollapsed(!isPromptLibraryCollapsed)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsPromptLibraryCollapsed(!isPromptLibraryCollapsed);
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }}
            >
              <LightbulbFilament24Regular className={styles.promptLibraryIcon} />
              <span className={styles.promptLibraryTitle}>Prompt Library for {area.name}</span>
              <span className={styles.promptLibraryToggle}>
                {isPromptLibraryCollapsed ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
              </span>
            </div>
            <Button
              appearance="primary"
              size="small"
              onClick={() => setIsAddPromptModalOpen(true)}
              style={{
                marginLeft: '12px',
                backgroundColor: '#107C10',
                border: 'none'
              }}
            >
              + Add Custom Prompt
            </Button>
          </div>
          <div className={`${styles.promptsWrapper} ${isPromptLibraryCollapsed ? styles.promptsWrapperCollapsed : ''}`}>
            <PromptList
              prompts={area.prompts}
              onSelectPrompt={handlePromptSelect}
              onEditPrompt={handleEditPrompt}
              onDeletePrompt={handleDeletePrompt}
              areaName={area.name}
              areaColor={area.color}
            />
          </div>
        </div>
      </div>

      {/* Add Prompt Modal */}
      <AddPromptModal
        open={isAddPromptModalOpen}
        onClose={() => setIsAddPromptModalOpen(false)}
        areas={areas}
        onPromptAdded={async () => {
          // Reload areas to get updated prompts
          try {
            const areasData = await configService.getAreas();
            setAreas(areasData);
            // Force parent to reload if needed
            window.location.reload();
          } catch (error) {
            console.error('Failed to reload areas:', error);
          }
        }}
      />

      {/* Edit Prompt Modal */}
      <EditPromptModal
        open={isEditPromptModalOpen}
        onClose={() => {
          setIsEditPromptModalOpen(false);
          setEditingPrompt(null);
        }}
        prompt={editingPrompt}
        onPromptUpdated={async () => {
          // Reload areas to get updated prompts
          try {
            const areasData = await configService.getAreas();
            setAreas(areasData);
            // Force parent to reload if needed
            window.location.reload();
          } catch (error) {
            console.error('Failed to reload areas:', error);
          }
        }}
      />
    </div>
  );
};
